import { useCallback, useEffect, useMemo, useState } from 'react';

import { Delete02Icon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import {
  IDV_OVERALL_STATUS_DISPLAY,
  IDV_SUB_STATUS,
  IDV_SUB_STATUS_DISPLAY,
  IDV_UPDATABLE_STATUS_FIELDS,
  NAME_CHANGE_DOCUMENTATION_ID_TYPES,
} from 'constants/idv';
import { AdminRoles } from 'constants/permissions';
import { parseIdKey } from 'helpers/ids';
import { useDocumentTitle } from 'hooks/document-title';
import { useIdVerificationSummary } from 'hooks/id-verification-summary';
import { useLocale } from 'hooks/locale';
import { useApiUploadActions } from 'hooks/upload-actions';
import { useVerifyIdDocument } from 'hooks/verify-id-document';
import AdminPage from 'layouts/AdminPage/AdminPage';
import { Tab, Tabs } from 'react-bootstrap';
import cookie from 'react-cookies';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { AddNoteModal } from './components/AddNoteModal/AddNoteModal';
import { AddNotificationModal } from './components/AddNotificationModal/AddNotificationModal';
import { CandidateCheckDetails } from './components/CandidateCheckDetails/CandidateCheckDetails';
import { CandidateCheckValues } from './components/CandidateCheckValues/CandidateCheckValues';
import { CandidateDocumentUploadModal } from './components/CandidateDocumentUpload/CandidateDocumentUploadModal';
import { CandidateUpdateModal } from './components/CandidateUpdateModal/CandidateUpdateModal';
import { FilePreview } from './components/FilePreview/FilePreview';
import { IdVerificationModal } from './components/IdVerificationModal/IdVerificationModal';
import { RejectModal } from './components/RejectModal/RejectModal';
import { ActionsMenu } from 'components/ActionsMenu/ActionsMenu';
import Alert from 'components/Alert/Alert';
import { CollapsibleBlock } from 'components/CollapsibleBlock/CollapsibleBlock';
import { Button } from 'components/FormComponents';
import { InputGroup } from 'components/FormComponents/InputGroup/InputGroup';
import { Select } from 'components/FormComponents/Select/Select';
import { ToggleSwitch } from 'components/FormComponents/ToggleSwitch/ToggleSwitch';
import { IconButton } from 'components/IconButton/IconButton';
import { IconStatusButton } from 'components/IconStatusButton/IconStatusButton';
import StatusPill from 'components/Pills/StatusPills';
import { RowDetails } from 'components/RowDetails/RowDetails';
import { CustomToast } from 'components/Toast/CustomToast';

import { loginAs } from 'api/authentication-slice';
import {
  useRejectCandidateChecksValuesMutation,
  useUpdateCandidateChecksValuesMutation,
} from 'api/candidate-checks-values-api';
import {
  useAddCandidateNoteMutation,
  useAddCandidateNotificationMutation,
  useCancelCandidateChecksMutation,
  useGetCandidateChecksQuery,
  useGetCandidateChecksValuesQuery,
  useGetCandidateCompanyQuery,
  useGetCandidateHistoryQuery,
  useGetCandidateQuery,
  useGetCandidateRefereesQuery,
  useGetCandidateUploadsQuery,
  useLazyGetCandidateHistoryQuery,
  useUpdateCandidateMutation,
  useUpdateIdUploadDetailMutation,
} from 'api/candidates-api';
import { useGetCheckTypesQuery } from 'api/check-types-api';
import { useGetCompanyChecksQuery } from 'api/company-api';

import styles from './AdminCandidate.module.scss';
import { AdminCandidateHistoryTab } from './tabs/AdminCandidateHistoryTab';

const IdVerificationForm = ({
  upload,
  linkToNameChangeDocument = false,
  nameChangeDocumentUploads = [],
  idUploadDetail,
  getStatusBoolean,
  onIdStatusUpdate,
  sectionId,
  sectionProps,
  onDelete,
  t,
}) => {
  // Filter out fields that are 'not_applicable'
  const editableStatusFields = IDV_UPDATABLE_STATUS_FIELDS.filter(
    fieldName => idUploadDetail?.[fieldName] !== IDV_SUB_STATUS.NOT_APPLICABLE,
  );

  // Build default values only for editable fields
  const defaultValues = {};
  editableStatusFields.forEach(fieldName => {
    defaultValues[fieldName] = getStatusBoolean(idUploadDetail?.[fieldName]);
  });
  // Include name_linkage_upload_id if linkToNameChangeDocument is enabled
  if (linkToNameChangeDocument) {
    defaultValues.name_linkage_upload_id = idUploadDetail?.name_linkage_upload_id || null;
  }

  const { register, handleSubmit, watch, setValue } = useForm({
    defaultValues,
  });
  const nameLinkageUploadId = watch('name_linkage_upload_id');

  const onSubmit = data => {
    onIdStatusUpdate(upload.id, data);
  };

  // Get status display helper for overall status
  const getOverallStatusDisplay = status => {
    if (!status) return null;
    return IDV_OVERALL_STATUS_DISPLAY[status] || { title: status, color: 'neutral' };
  };

  // Get status display helper for sub-statuses
  const getSubStatusDisplay = status => {
    if (!status) return null;
    return IDV_SUB_STATUS_DISPLAY[status] || { title: status, color: 'neutral' };
  };

  const overallStatus = idUploadDetail?.status;
  const faceSimilarityStatus = idUploadDetail?.face_similarity_status;
  const overallStatusDisplay = getOverallStatusDisplay(overallStatus);
  const faceSimilarityDisplay = getSubStatusDisplay(faceSimilarityStatus);

  return (
    <div className="d-flex flex-column u-gap u-border u-padding u-border-radius">
      <FilePreview file={upload} {...sectionProps} onDelete={onDelete} />
      {sectionId === 'candidateIds' && idUploadDetail && (
        <>
          {overallStatusDisplay && (
            <div className="d-flex align-items-center u-gap-small">
              <span className="u-font-weight-600">Overall Status:</span>
              <StatusPill text={overallStatusDisplay.title} status={overallStatusDisplay.color} />
            </div>
          )}
          {faceSimilarityStatus && faceSimilarityStatus !== IDV_SUB_STATUS.NOT_APPLICABLE && (
            <div className="d-flex align-items-center u-gap-small">
              <span className="u-font-weight-600">
                {t('admin.candidate.idVerification.face_similarity_status')}:
              </span>
              <StatusPill text={faceSimilarityDisplay.title} status={faceSimilarityDisplay.color} />
            </div>
          )}
          {editableStatusFields.length > 0 && (
            <form onSubmit={handleSubmit(onSubmit)}>
              {editableStatusFields.map(fieldName => {
                const status = idUploadDetail?.[fieldName];
                const label = t(`admin.candidate.idVerification.${fieldName}`) + ':';
                const statusDisplay = getSubStatusDisplay(status);
                return (
                  <ToggleSwitch
                    key={fieldName}
                    name={fieldName}
                    register={register}
                    label={label}
                    status={
                      statusDisplay
                        ? { text: statusDisplay.title, status: statusDisplay.color }
                        : null
                    }
                  />
                );
              })}
              {linkToNameChangeDocument && (
                <InputGroup title="Link to Name Change Document">
                  <Select
                    name="name_linkage_upload_id"
                    placeholder="Select your name change document"
                    register={register}
                    setValue={setValue}
                    value={nameLinkageUploadId}>
                    {(nameChangeDocumentUploads || []).map(option => {
                      const idType =
                        option?.id_upload_detail?.selected_id_type || option?.metadata?.id_type;
                      const idTypeName = t(`${idType}.name`, {
                        ns: 'id_document_types',
                        returnObjects: true,
                      });
                      return (
                        <Select.Item key={option.id} value={option.id}>
                          {idTypeName} ({option?.file_name})
                        </Select.Item>
                      );
                    })}
                  </Select>
                </InputGroup>
              )}
              <Button submit={true}>Update</Button>
            </form>
          )}
        </>
      )}
    </div>
  );
};

const AdminCandidate = () => {
  let { candidate_id: candidateId } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation(['translation', 'id_document_types']);

  const { data: company, refetch: refetchCompany } = useGetCandidateCompanyQuery(candidateId, {
    refetchOnMountOrArgChange: true,
  });
  const { data: companyChecksData, refetch: refetchCompanyChecks } = useGetCompanyChecksQuery(
    company?.id,
    {
      skip: !company?.id,
      refetchOnMountOrArgChange: true,
    },
  );
  const { data: candidateData, refetch: refetchCandidate } = useGetCandidateQuery(candidateId, {
    refetchOnMountOrArgChange: true,
  });
  const {
    data: checksData,
    isLoading: loadingChecks,
    refetch: refetchChecks,
  } = useGetCandidateChecksQuery(candidateId);
  const { data: checkTypes } = useGetCheckTypesQuery();
  const { data: checkValuesData } = useGetCandidateChecksValuesQuery(candidateId);
  const { data: uploadsData, refetch: refetchUploads } = useGetCandidateUploadsQuery({
    id: candidateId,
    params: { with_metadata: true },
  });

  const {
    isLoading: isIdvLoading,
    idvStatus,
    idvStatusKey,
    faceSimilarity,
    selfie,
    idDoc,
    idDocInfo,
    refetch: refetchIdv,
  } = useIdVerificationSummary(candidateId);
  const idvSummary = { idvStatus, idvStatusKey, faceSimilarity, selfie, idDoc, idDocInfo };

  const { data: refereesData } = useGetCandidateRefereesQuery(candidateId);
  const { data: checkNotesData, refetch: refetchCheckNotes } = useGetCandidateHistoryQuery({
    id: candidateId,
    params: { internal: true },
  });

  const [getCandidateHistory] = useLazyGetCandidateHistoryQuery();
  const [rejectCandidateChecksValues] = useRejectCandidateChecksValuesMutation();
  const [updateCandidateChecksValues] = useUpdateCandidateChecksValuesMutation();
  const [updateCandidate] = useUpdateCandidateMutation();
  const [cancelCandidateChecks] = useCancelCandidateChecksMutation();
  const [addCandidateNote] = useAddCandidateNoteMutation();
  const [addCandidateNotification] = useAddCandidateNotificationMutation();
  const [updateIdUploadDetail] = useUpdateIdUploadDetailMutation();
  const { verify: verifyIdDocument, isVerifying: isVerifyingId } = useVerifyIdDocument(
    { candidateId },
    {
      onSuccess: async () => {
        await Promise.all([refetchIdv(), refetchUploads()]);
      },
    },
  );

  const [loading, setLoading] = useState(false);

  const [cancelConfirmation, setCancelConfirmation] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [restoreConfirmation, setRestoreConfirmation] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [addNoteModal, setAddNoteModal] = useState(false);
  const [addNotificationModal, setAddNotificationModal] = useState(false);
  const [updateCandidateModal, setUpdateCandidateModal] = useState(false);
  const [showIdVerificationModal, setShowIdVerificationModal] = useState(false);
  const [showDocumentUploadModal, setShowDocumentUploadModal] = useState(false);

  const [candidate, setCandidate] = useState();
  const [checks, setChecks] = useState([]);
  const [companyChecks, setCompanyChecks] = useState([]);
  const [referees, setReferees] = useState([]);
  const [rejectedUploads, setRejectedUploads] = useState([]);
  const [checkValues, setCheckValues] = useState([]);
  const [idUploads, setIdUploads] = useState([]);
  const [selfieUpload, setSelfieUpload] = useState(null);
  const [signatureUploads, setSignatureUploads] = useState([]);
  const [supportUploads, setSupportUploads] = useState([]);
  const [resultUploads, setResultUploads] = useState([]);

  const [historyData, setHistoryData] = useState([]);

  const { dateSlashFormat } = useLocale(company?.locale);

  const { deleteUpload } = useApiUploadActions();

  const [deleteUploadId, setDeleteUploadId] = useState(null);
  const [showDeleteUploadAlert, setShowDeleteUploadAlert] = useState(false);

  const getCheckNotes = useCallback(
    checkId => {
      return (checkNotesData || []).filter(note => note.candidate_check_id === checkId);
    },
    [checkNotesData],
  );

  const fetchHistoryData = useCallback(async () => {
    if (candidate?.id) {
      const { data } = await getCandidateHistory({ id: candidate.id });
      setHistoryData(data || []);
    }
  }, [candidate?.id, getCandidateHistory, checkNotesData]);

  useDocumentTitle(candidate ? candidate?.name : 'Loading...');

  useEffect(() => {
    if (candidateData) setCandidate(candidateData);
  }, [candidateData]);
  useEffect(() => {
    if (checkValuesData) {
      setCheckValues({
        ...checkValuesData?.check_values,
        candidate_fields: checkValuesData?.candidate_fields,
        check_type_id_requirements: checkValuesData?.check_type_id_requirements,
      });
    }
  }, [checkValuesData]);
  useEffect(() => {
    if (checksData) setChecks(checksData);
  }, [checksData]);
  useEffect(() => {
    if (companyChecksData) setCompanyChecks(companyChecksData);
  }, [companyChecksData]);
  useEffect(() => {
    if (refereesData) setReferees(refereesData);
  }, [refereesData]);
  useEffect(() => {
    if (!uploadsData) return;
    const rejectedFiles = checkValuesData?.rejected_values?.files || [];
    const rejectedSet = new Set(rejectedFiles.map(id => String(id)));
    const isRejected = u => rejectedSet.has(String(u.id));

    setRejectedUploads((uploadsData || []).filter(upload => isRejected(upload)));

    setIdUploads(
      (uploadsData || []).filter(upload => upload.metadata?.id_type && !isRejected(upload)),
    );
    setSelfieUpload((uploadsData || []).find(upload => upload.metadata?.selfie) || null);
    setSignatureUploads(
      (uploadsData || []).filter(
        upload => upload.metadata?.signature || upload.metadata?.guardian_signature,
      ),
    );

    setSupportUploads(
      (uploadsData || []).filter(upload => upload.metadata?.check_type || upload.metadata?.bot),
    );
    setResultUploads(
      (uploadsData || []).filter(upload => upload.metadata?.result || upload.metadata?.sub_result),
    );

    refetchChecks();
  }, [uploadsData, checkValuesData?.rejected_values?.files]);

  const isNameChangeDocumentUploaded = useMemo(() => {
    return (idUploads || []).filter(upload => {
      const { idType } = parseIdKey(upload?.metadata?.id_type);
      return idType && NAME_CHANGE_DOCUMENTATION_ID_TYPES.includes(idType);
    });
  }, [idUploads]);

  // Cancel
  const cancelCheck = async () => {
    setLoading(true);
    setCancelConfirmation(false);
    const { data: result } = await cancelCandidateChecks(candidateId);
    setCandidate(result);
    setLoading(false);
  };

  // Reject
  const handleReject = async fields => {
    setLoading(true);
    const { data: result } = await rejectCandidateChecksValues({
      candidate_id: candidateId,
      rejected_fields: fields,
    });
    setCheckValues(result);
    setLoading(false);
  };

  // Update
  const handleUpdate = async data => {
    setLoading(true);
    const { data: result } = await updateCandidate({ id: candidateId, data });
    setCandidate(result);
    setLoading(false);
  };

  // Update candidate checks values
  const handleUpdateCandidateChecksValues = async data => {
    setLoading(true);
    const { data: result } = await updateCandidateChecksValues({
      candidate_id: candidateId,
      values: data,
    });
    setCheckValues(result);
    setLoading(false);
  };

  // Login as client user
  const loginAsClient = async () => {
    setLoading(true);
    const { payload: result } = await dispatch(loginAs(candidate.user_id));
    cookie.save('admin-token', cookie.load('token'), { path: '/' });
    cookie.save('admin-route', `/admin/candidates/${candidate.id}`, { path: '/' });
    cookie.save('token', result.token, { path: '/' });
    setLoading(false);
    navigate(`/check/${candidate.id}/background_checks`);
  };

  // Update check data
  const addOrUpdateCheck = check => {
    if (!check?.id) return;

    if (checks.findIndex(c => c.id === check.id) >= 0)
      setChecks(curr => curr.map(c => (c.id === check.id ? check : c)));
    else setChecks(curr => [...curr, check]);
    refetchChecks();
    refetchCandidate();
    refetchCompany();
    refetchCompanyChecks();
  };

  // Restore candidate
  const restoreCandidate = () => {
    setRestoreConfirmation(false);
    handleUpdate({ deleted: false });
  };

  // Delete candidate
  const deleteCandidate = () => {
    setDeleteConfirmation(false);
    handleUpdate({ deleted: true });
  };

  // Update candidate details
  const updateCandidateDetails = data => {
    setUpdateCandidateModal(false);
    handleUpdate(data);
  };

  // Add candidate note
  const handleAddCandidateNote = async ({ message }) => {
    setLoading(true);
    setAddNoteModal(false);
    await addCandidateNote({ id: candidateId, message });
    setLoading(false);
  };

  // Add candidate note
  const handleAddCandidateNotification = async params => {
    try {
      setLoading(true);
      await addCandidateNotification({ id: candidateId, params });
      setLoading(false);
      setAddNotificationModal(false);
      toast.custom(
        t => (
          <CustomToast
            t={t}
            title="Nicely done - Email sent"
            message={`Email sent successfully to ${params.recipient}.`}
            type="success"
          />
        ),
        {
          duration: 5000,
        },
      );
    } catch (error) {
      toast.custom(
        t => <CustomToast t={t} title={`Oops - ${error.message || error}`} type="error" />,
        {
          duration: 5000,
        },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUpload = async id => {
    await deleteUpload(id);
    refetchUploads();
    setShowDeleteUploadAlert(false);
    setDeleteUploadId(null);
  };

  const confirmDeleteUpload = id => {
    setDeleteUploadId(id);
    setShowDeleteUploadAlert(true);
  };

  const handleUpdateIDVerificationStatus = async (uploadId, data) => {
    // Convert boolean values to status strings
    const statusData = {};

    const nameLinkageUploadId = data?.name_linkage_upload_id || null;

    IDV_UPDATABLE_STATUS_FIELDS.forEach(fieldName => {
      if (data[fieldName] !== undefined) {
        statusData[fieldName] = data[fieldName] ? IDV_SUB_STATUS.PASSED : IDV_SUB_STATUS.FAILED;
      }
    });

    try {
      setLoading(true);

      await updateIdUploadDetail({
        id: candidateId,
        uploadId,
        data: { ...statusData, name_linkage_upload_id: nameLinkageUploadId },
      }).unwrap();

      await Promise.all([refetchUploads(), refetchIdv()]);

      toast.custom(
        t => (
          <CustomToast
            t={t}
            title="Success"
            message="ID verification status updated successfully"
            type="success"
          />
        ),
        {
          duration: 3000,
        },
      );
    } catch (error) {
      toast.custom(
        t => (
          <CustomToast
            t={t}
            title="Error"
            message={error.message || 'Failed to update ID verification status'}
            type="error"
          />
        ),
        {
          duration: 5000,
        },
      );
    } finally {
      setLoading(false);
    }
  };

  // Verify ID
  const handleVerifyIdDocument = async ({
    uploadId,
    idCode,
    idType,
    idCountry = null,
    expiryTolerance = null,
    selfieUploadId = null,
  }) => {
    setLoading(true);
    setShowIdVerificationModal(false);
    try {
      if (!uploadId || !idType) throw new Error('uploadId and idType are required');
      await verifyIdDocument({
        uploadId,
        idType,
        idCountry,
        expiryTolerance,
        selfieUploadId,
      });
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const handleAfterUpload = async (createdUpload, selectedIdType, selectedCountryAlpha2) => {
    const hasSelfie = !!selfieUpload?.id;

    // Always verify IDs uploaded from admin page to create id_upload_detail records
    // For photo IDs, include selfie if available; for non-photo IDs, verify without selfie
    if (createdUpload?.id && selectedIdType?.value) {
      try {
        await handleVerifyIdDocument({
          uploadId: createdUpload.id,
          idType: selectedIdType.value,
          idCountry: selectedCountryAlpha2 || null,
          expiryTolerance: null,
          selfieUploadId: hasSelfie ? selfieUpload.id : null,
        });
        // Ensure uploads are refetched after verification completes
        await Promise.all([refetchUploads(), refetchIdv()]);
      } catch (error) {
        console.error('Error verifying ID document:', error);
        // Still refetch uploads even if verification fails, in case the upload was created
        await refetchUploads();
      }
    }
  };

  const actions = useMemo(() => {
    return [
      {
        icon: 'settings',
        title: t('admin.candidate.updateCandidateDetails'),
        onClick: () => setUpdateCandidateModal(true),
      },
      {
        icon: 'x',
        title: t('common.cancelCandidate'),
        onClick: () => setCancelConfirmation(true),
      },
      {
        icon: 'user-x',
        title: t('admin.candidate.declineBackgroundDetails'),
        onClick: () => setRejectModal(true),
      },
      {
        icon: 'key',
        title: t('admin.candidate.loginAsClient'),
        onClick: loginAsClient,
      },
      {
        icon: 'external-link',
        title: t('admin.candidate.openCandidateLink'),
        onClick: () =>
          window.open(
            `${process.env.REACT_APP_WEB_URL}/form_submission/candidate/dashboard/${candidate.token}?admin=true`,
            '_blank',
          ),
        disabled: !candidate?.token,
      },
      {
        icon: 'external-link',
        title: t('admin.candidate.openPartnerLink'),
        onClick: () => window.open(candidate.partner_link, '_blank'),
        disabled: !candidate?.partner_link,
      },
      {
        icon: 'edit',
        title: t('common.addNote'),
        onClick: () => setAddNoteModal(true),
      },
      {
        icon: 'mail',
        title: t('common.sendEmail'),
        onClick: () => setAddNotificationModal(true),
      },
      {
        icon: 'trash',
        title: t('common.delete'),
        onClick: () => setDeleteConfirmation(true),
        hide: candidate?.deleted,
      },
      {
        icon: 'refresh-ccw',
        title: t('common.restore'),
        onClick: () => setRestoreConfirmation(true),
        hide: !candidate?.deleted,
      },
    ];
  }, [candidate]);

  const UPLOAD_SECTIONS = [
    {
      id: 'candidateIds',
      title: 'admin.candidate.candidateIds',
      uploads: [selfieUpload, ...idUploads, ...signatureUploads].filter(Boolean),
      startOpen: true,
      headerActionIcon: 'upload',
      showHeaderAction: true,
      onClick: {
        headerAction: () => setShowDocumentUploadModal(true),
        delete: confirmDeleteUpload,
      },
      props: {
        allowDelete: true,
      },
    },
    {
      id: 'supportingDocuments',
      title: 'admin.candidate.supportingDocuments',
      uploads: supportUploads,
      startOpen: null,
      onClick: {
        delete: confirmDeleteUpload,
      },
      props: {
        allowDelete: true,
      },
    },
    {
      id: 'resultUploads',
      title: 'common.results',
      uploads: resultUploads,
      startOpen: null,
      onClick: {
        delete: confirmDeleteUpload,
      },
      props: {
        allowDelete: true,
      },
    },
    {
      id: 'rejectedUploads',
      title: 'admin.candidate.rejectedUploads',
      uploads: rejectedUploads,
      startOpen: false,
      props: {
        rejected: true,
        allowDelete: false,
      },
    },
  ];

  return (
    <AdminPage
      loading={loading || loadingChecks || isVerifyingId || isIdvLoading}
      title={candidate?.name}
      copyToClipboard={true}
      allowedRoles={[AdminRoles.SuperAdmin, AdminRoles.Support, AdminRoles.ACIC]}>
      <Alert
        show={cancelConfirmation}
        title={t('admin.candidate.cancelCandidate.title', { title: candidate?.first_name })}
        ok="Yes"
        cancel="No"
        onOk={cancelCheck}
        onCancel={() => setCancelConfirmation(false)}
      />

      <Alert
        show={deleteConfirmation}
        title={t('admin.candidate.deleteCandidate.title', { title: candidate?.first_name })}
        ok="Delete"
        type="error"
        icon={Delete02Icon}
        showConfirmationInput
        onOk={deleteCandidate}
        cancel="Cancel"
        onCancel={() => setDeleteConfirmation(false)}
      />

      <Alert
        show={restoreConfirmation}
        title={t('admin.candidate.restoreCandidate.title', { title: candidate?.first_name })}
        ok="Restore"
        onOk={restoreCandidate}
        cancel="Cancel"
        onCancel={() => setRestoreConfirmation(false)}
      />

      <Alert
        show={showDeleteUploadAlert}
        title={t('admin.candidate.deleteUpload.title')}
        ok="Delete"
        type="error"
        icon={Delete02Icon}
        onOk={() => handleDeleteUpload(deleteUploadId)}
        cancel="Cancel"
        onCancel={() => setShowDeleteUploadAlert(false)}
      />

      <RejectModal
        visible={rejectModal}
        checkValues={checkValues?.values}
        uploads={[selfieUpload, ...idUploads]}
        onClose={() => setRejectModal(false)}
        onReject={handleReject}
      />

      <IdVerificationModal
        idvSummary={idvSummary}
        visible={showIdVerificationModal}
        onClose={() => setShowIdVerificationModal(false)}
      />

      <AddNoteModal
        visible={addNoteModal}
        onSubmit={handleAddCandidateNote}
        onClose={() => setAddNoteModal(false)}
      />

      <AddNotificationModal
        visible={addNotificationModal}
        modelParams={{
          candidate_id: candidateId,
        }}
        onSubmit={handleAddCandidateNotification}
        onClose={() => setAddNotificationModal(false)}
      />

      <CandidateUpdateModal
        candidate={candidate}
        visible={updateCandidateModal}
        onSubmit={updateCandidateDetails}
        onClose={() => setUpdateCandidateModal(false)}
      />

      <RowDetails className={styles.details}>
        <RowDetails.Status status={candidate?.status} />
        <RowDetails.Column title={t('common.id')} data={candidate?.id} />
        <RowDetails.Column title={t('common.email')} data={candidate?.email} />
        <RowDetails.Column title={t('common.company')} data={company?.name} />
        <RowDetails.Column title={t('common.department')} data={candidate?.department || 'N/A'} />
        <RowDetails.Column title={t('common.jobRole')} data={candidate?.job_role || 'N/A'} />
        <RowDetails.Column
          title={t('common.createdOn')}
          data={<Moment format={dateSlashFormat}>{candidate?.created_at}</Moment>}
        />
        <RowDetails.Column
          title={t('common.submittedOn')}
          data={<Moment format={dateSlashFormat}>{candidate?.submitted_at}</Moment>}
        />
        <RowDetails.Column
          title={t('common.completedOn')}
          data={
            candidate?.completed_at ? (
              <Moment format={dateSlashFormat}>{candidate?.completed_at}</Moment>
            ) : (
              '-'
            )
          }
        />
        <RowDetails.Column
          title={t('common.dataDeletedAt')}
          data={
            candidate?.data_deleted_at ? (
              <Moment format={dateSlashFormat}>{candidate?.data_deleted_at}</Moment>
            ) : (
              '-'
            )
          }
        />
        <IconStatusButton
          icon="id-badge"
          prefix="fa"
          tooltip={t(idvStatusKey)}
          status={idvStatus}
          onClick={() => setShowIdVerificationModal(true)}
        />
        <ActionsMenu>
          {actions.map((action, index) =>
            action.hide ? null : (
              <ActionsMenu.Item
                key={action.title + index}
                icon={action.icon}
                title={action.title}
                onClick={action.onClick}
                disabled={action.disabled}
              />
            ),
          )}
        </ActionsMenu>
      </RowDetails>

      <Tabs defaultActiveKey="checks" className="tabs no-padding padding-small" mountOnEnter>
        <Tab eventKey="checks" title="Candidate Checks">
          <div className={styles.section}>
            <div className={styles.checks}>
              {(checks || []).map(check => {
                const checkType = checkTypes?.find(checkType => checkType.slug === check.type);

                return (
                  <CandidateCheckDetails
                    key={check.id}
                    candidate={candidate}
                    referees={referees}
                    check={check}
                    checkType={checkType}
                    companyChecks={companyChecks}
                    checkValues={checkValues}
                    setLoading={setLoading}
                    addOrUpdateCheck={addOrUpdateCheck}
                    onDocumentUpload={refetchUploads}
                    checkNotes={getCheckNotes(check?.id)}
                    refetchSupportNotes={refetchCheckNotes}
                    supportUploads={supportUploads}
                    resultUploads={resultUploads}
                  />
                );
              })}
            </div>
          </div>

          <div className={cn(styles.section, 'd-flex', 'align-items-start')}>
            <CollapsibleBlock className="u-margin-right--large u-width-50" startOpen={true}>
              <CollapsibleBlock.Header>
                <h1 className={styles.title}>{t('admin.candidate.candidateValues')}</h1>
              </CollapsibleBlock.Header>
              <CollapsibleBlock.Content>
                <div className="u-margin-y">
                  <CandidateCheckValues
                    checkValues={checkValues}
                    updateCandidateChecksValues={handleUpdateCandidateChecksValues}
                  />
                </div>
              </CollapsibleBlock.Content>
            </CollapsibleBlock>

            <div className="u-width-50">
              {UPLOAD_SECTIONS.map(section => (
                <CollapsibleBlock
                  key={section.id}
                  className={section.id !== 'rejectedUploads' ? 'u-margin-bottom' : ''}
                  startOpen={
                    section.startOpen === null
                      ? section.uploads && section.uploads.length > 0
                      : section.startOpen
                  }>
                  <CollapsibleBlock.Header>
                    <h1 className={styles.title}>{t(section.title)}</h1>
                    {section.showHeaderAction && (
                      <IconButton
                        icon={section.headerActionIcon}
                        onClick={section.onClick.headerAction}
                      />
                    )}
                  </CollapsibleBlock.Header>
                  <CollapsibleBlock.Content>
                    <div className="u-margin-y d-flex flex-wrap gap-default">
                      {(section.uploads || []).map(upload => {
                        const idUploadDetail = upload.id_upload_detail;
                        const getStatusBoolean = status => status === IDV_SUB_STATUS.PASSED;

                        const canLinkToNameChangeDocument =
                          isNameChangeDocumentUploaded?.length > 0 &&
                          upload.id_upload_detail?.selected_id_type &&
                          !NAME_CHANGE_DOCUMENTATION_ID_TYPES.includes(
                            upload.id_upload_detail.selected_id_type,
                          );

                        return (
                          <IdVerificationForm
                            key={upload.id}
                            upload={upload}
                            idUploadDetail={idUploadDetail}
                            getStatusBoolean={getStatusBoolean}
                            onIdStatusUpdate={handleUpdateIDVerificationStatus}
                            sectionId={section.id}
                            sectionProps={section.props}
                            linkToNameChangeDocument={canLinkToNameChangeDocument}
                            nameChangeDocumentUploads={isNameChangeDocumentUploaded}
                            onDelete={
                              section.props.allowDelete
                                ? () => section.onClick.delete(upload.id)
                                : undefined
                            }
                            t={t}
                          />
                        );
                      })}
                    </div>
                  </CollapsibleBlock.Content>
                </CollapsibleBlock>
              ))}
            </div>
          </div>
          <CandidateDocumentUploadModal
            candidate={candidate}
            company={company}
            visible={showDocumentUploadModal}
            onUpdate={refetchUploads}
            onClose={() => setShowDocumentUploadModal(false)}
            onAfterUpload={handleAfterUpload}
          />
        </Tab>
        <Tab eventKey="history" title="History">
          <AdminCandidateHistoryTab
            candidate={candidate}
            company={company}
            companyChecks={companyChecks}
            referees={referees}
            historyData={historyData}
            fetchHistoryData={fetchHistoryData}
          />
        </Tab>
      </Tabs>
    </AdminPage>
  );
};

export default AdminCandidate;
